import React, { useState, useEffect, useRef, useMemo } from "react";
import { use100vh } from "react-div-100vh";
import { createGlobalStyle } from "styled-components";
import Chip from "@mui/material/Chip";
import { Virtuoso } from "react-virtuoso";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ChecklistIcon from "@mui/icons-material/Checklist";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Box } from "@mui/system";
import { motion } from "framer-motion";
import EventDataModalV2 from "../Calendar/EventDataModalV2";
import Slide from "@mui/material/Slide";
import {
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Button,
  Tooltip,
  ListItemIcon,
  Avatar,
  AvatarGroup,
  Collapse,
  List as MUIList,
  ListItemButton,
  ListItemText,
  ListItem,
  Checkbox,
  Paper,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  db,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  collection,
  orderBy,
  query,
  addDoc,
  setDoc,
} from "../firebaseConfig";
import { v4 as uuidv4 } from "uuid";
import LabelChip from "../GlobalComponents/LabelChip";
import PermanentLabelChip from "../GlobalComponents/PermanentLabelChip";
import CloseIcon from "@mui/icons-material/Close";
import DueDateChip from "../GlobalComponents/DueDateChip";
import ImagesChip from "../GlobalComponents/ImagesChip";
import KanbanClientSelector from "./KanbanClientSelector";
import KanbanDueDateExpressContainer from "./KanbanDueDateExpressContainer";
import KanbanJobNumber from "./KanbanJobNumber";
import KanbanJobTypeSelector from "./KanbanJobTypeSelector";
import KanbanSpecialRequirementsContainer from "./KanbanSpecialRequirementsContainer";
import Dialog from "@mui/material/Dialog";
import DialogContentText from "@mui/material/DialogContentText";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useLocation } from "react-router-dom";
import "./kanban.css";
import { useAtom } from "jotai";
import {
  isIdleAtom,
  themeAtom,
  listsAtom,
  labelColoursAtom,
  staffDataAtom,
  currentStaffDataAtom,
  newJobObjectAtom,
  isAdminAtom,
  jobTypesAtom,
  shouldPauseSubscriptionsAtom,
} from "../-Atoms";
import getJobName from "../GlobalFunctions/getJobName";

import { checkInvoiceStatus } from "../GlobalFunctions/checkInvoiceStatus";

const { DateTime } = require("luxon");

const GlobalStyles = createGlobalStyle``;

function reorder(list, startIndex, endIndex) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

// Helper function to retry Firebase updates with exponential backoff
async function retryFirebaseUpdate(
  updateFunction,
  maxRetries = 3,
  baseDelay = 1000
) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      await updateFunction();
      return; // Success, exit early
    } catch (error) {
      console.warn(`Firebase update attempt ${attempt + 1} failed:`, error);

      if (attempt === maxRetries - 1) {
        // Last attempt failed, throw the error
        throw error;
      }

      // Wait before retrying with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      console.log(`Retrying in ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
}

// ponytail: swap-with-neighbour reorder instead of full column drag-and-drop, add nested Droppable types when someone actually asks for drag-to-reorder columns
const ListEditControls = ({
  list,
  index,
  totalLists,
  onMove,
  onDelete,
  onRename,
}) => {
  const [theme] = useAtom(themeAtom);
  const canDelete = (list.items?.length || 0) === 0;

  const buttonSx = {
    padding: "3px",
    borderRadius: "6px",
    color: theme === "dark" ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)",
    "&:hover": {
      backgroundColor:
        theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
      color: theme === "dark" ? "#ffffff" : "#1a202c",
    },
    "&.Mui-disabled": {
      color: theme === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)",
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "4px",
        paddingTop: "4px",
        borderTop: `1px solid ${
          theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"
        }`,
      }}
    >
      <Tooltip title="Move left">
        <span>
          <IconButton
            size="small"
            sx={buttonSx}
            disabled={index === 0}
            onClick={() => onMove(list.id, "left")}
          >
            <ChevronLeftIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>
        </span>
      </Tooltip>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Tooltip title="Rename list">
          <IconButton
            size="small"
            sx={buttonSx}
            onClick={() => onRename(list.id)}
          >
            <EditOutlinedIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={canDelete ? "Delete list" : "List must be empty to delete"}
        >
          <span>
            <IconButton
              size="small"
              disabled={!canDelete}
              onClick={() => onDelete(list.id)}
              sx={{
                ...buttonSx,
                "&:hover": {
                  backgroundColor: "rgba(211, 47, 47, 0.12)",
                  color: "#e5534b",
                },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: "1.05rem" }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Tooltip title="Move right">
        <span>
          <IconButton
            size="small"
            sx={buttonSx}
            disabled={index === totalLists - 1}
            onClick={() => onMove(list.id, "right")}
          >
            <ChevronRightIcon sx={{ fontSize: "1.1rem" }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function Kanban({ user }) {
  const [isIdle] = useAtom(isIdleAtom);
  const [theme] = useAtom(themeAtom);
  const [lists, setLists] = useAtom(listsAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [editMode, setEditMode] = useState(false);
  const [shouldPauseSubscriptions] = useAtom(shouldPauseSubscriptionsAtom);
  const urlQuery = useQuery();
  const [clientData, setClientData] = useState({});
  const isMobile = useMediaQuery("@media (max-width:780px)");
  const [modalJobData, setModalJobData] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [modalClientData, setModalClientData] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [modalOpen, setModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const modalWidth = isMobile ? "100vw" : "780px";

  const [eventDataModalV2Open, setEventDataModalV2Open] = useState(false);

  const [recentlyAssignedStaff, setRecentlyAssignedStaff] = useState([]);

  useEffect(() => {
    if (shouldPauseSubscriptions) {
      return;
    }

    const unsub = onSnapshot(
      doc(db, "AdminCollection", "recentlyAssignedStaff"),
      (doc) => {
        if (doc.exists()) {
          setRecentlyAssignedStaff(doc.data().recentlyAssignedStaff || []);
        } else {
          setRecentlyAssignedStaff([]);
        }
      }
    );
    return () => unsub();
  }, [shouldPauseSubscriptions]);

  useEffect(() => {
    if (shouldPauseSubscriptions) {
      return;
    }

    let unsub = onSnapshot(
      query(collection(db, "ClientDatabase"), orderBy("brand", "asc")),
      (collection) => {
        const cd = collection.docs.map((client) => {
          const data = client.data();
          data.id = client.id;
          return data;
        });
        setClientData([...cd]);
      }
    );
    return () => {
      unsub();
    };
  }, [setClientData, isIdle, shouldPauseSubscriptions]);

  const moveList = async (listId, direction) => {
    const idx = lists.findIndex((l) => l.id === listId);
    if (idx === -1) return;
    const swapWith = direction === "left" ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= lists.length) return;

    const newLists = [...lists];
    [newLists[idx], newLists[swapWith]] = [newLists[swapWith], newLists[idx]];
    setLists(newLists);
    await retryFirebaseUpdate(() =>
      updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
    );
  };

  // Inserts directly to the right of afterListId (appends if not found)
  const addList = async (afterListId) => {
    const name = window.prompt("New list name:");
    if (!name || !name.trim()) return;

    const idx = lists.findIndex((l) => l.id === afterListId);
    const insertAt = idx === -1 ? lists.length : idx + 1;

    const newLists = [...lists];
    newLists.splice(insertAt, 0, { id: uuidv4(), name: name.trim(), items: [] });
    setLists(newLists);
    await retryFirebaseUpdate(() =>
      updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
    );
  };

  const renameList = async (listId) => {
    const target = lists.find((l) => l.id === listId);
    if (!target) return;

    const name = window.prompt("Rename list:", target.name);
    if (!name || !name.trim() || name.trim() === target.name) return;

    const newLists = lists.map((l) =>
      l.id === listId ? { ...l, name: name.trim() } : l
    );
    setLists(newLists);
    await retryFirebaseUpdate(() =>
      updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
    );
  };

  const deleteList = async (listId) => {
    const target = lists.find((l) => l.id === listId);
    if (!target) return;
    if (target.items.length > 0) {
      alert("Cannot delete a list that still has cards in it.");
      return;
    }
    if (
      !window.confirm(`Delete list "${target.name}"? This cannot be undone.`)
    )
      return;

    const newLists = lists.filter((l) => l.id !== listId);
    setLists(newLists);
    await retryFirebaseUpdate(() =>
      updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
    );
  };

  async function onDragEnd(e) {
    if (!e?.destination?.droppableId) return;

    try {
      if (e.source.droppableId === e.destination.droppableId) {
        // Reordering within same list
        const listIndex = lists.findIndex(
          (list) => list.id === e.source.droppableId
        );
        if (listIndex === -1) {
          console.error("Cannot find list with ID:", e.source.droppableId);
          return;
        }

        // Create immutable copy of lists
        const newLists = [...lists];
        newLists[listIndex] = {
          ...newLists[listIndex],
          items: reorder(
            newLists[listIndex].items,
            e.source.index,
            e.destination.index
          ),
        };

        // Persist to Firebase with retry logic
        await retryFirebaseUpdate(() =>
          updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
        );

        console.log(
          `Successfully reordered item in list ${e.source.droppableId}`
        );
      } else {
        // Moving between different lists
        const sourceListIndex = lists.findIndex(
          (list) => list.id === e.source.droppableId
        );
        const destListIndex = lists.findIndex(
          (list) => list.id === e.destination.droppableId
        );

        if (sourceListIndex === -1) {
          console.error(
            "Cannot find source list with ID:",
            e.source.droppableId
          );
          return;
        }
        if (destListIndex === -1) {
          console.error(
            "Cannot find destination list with ID:",
            e.destination.droppableId
          );
          return;
        }

        const sourceList = lists[sourceListIndex];
        const destList = lists[destListIndex];
        const idJob = sourceList.items[e.source.index];

        // Create immutable copies
        const newLists = [...lists];
        newLists[sourceListIndex] = {
          ...sourceList,
          items: [...sourceList.items],
        };
        newLists[destListIndex] = {
          ...destList,
          items: [...destList.items],
        };

        // Remove from source
        newLists[sourceListIndex].items.splice(e.source.index, 1);

        // Add to destination (only if not already present)
        if (!newLists[destListIndex].items.includes(idJob)) {
          newLists[destListIndex].items.splice(e.destination.index, 0, idJob);
        }

        // Persist to Firebase with retry logic
        await retryFirebaseUpdate(() =>
          updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
        );

        console.log(
          `Successfully moved item ${idJob} from ${e.source.droppableId} to ${e.destination.droppableId}`
        );

        // Update job-specific properties based on destination list
        try {
          if (e.destination.droppableId === "5f081763cd97556d03075c86") {
            await retryFirebaseUpdate(() =>
              updateDoc(doc(db, "JobData", e.draggableId), {
                isInShooting: true,
                isInShootingTime: DateTime.now()
                  .setZone("Australia/Sydney")
                  .startOf("day")
                  .toUTC()
                  .toISO(),
              })
            );
          } else {
            await retryFirebaseUpdate(() =>
              updateDoc(doc(db, "JobData", e.draggableId), {
                isInShooting: false,
                isInShootingTime: null,
              })
            );
          }

          if (
            e.destination.droppableId === "0367cb77-644f-4d57-b4e8-4286148218d7"
          ) {
            const docSnap = await getDoc(doc(db, "JobData", e.draggableId));
            if (docSnap.exists()) {
              const jobData = docSnap.data();
              if (jobData.idLabels.indexOf("6285ea079131e28982c19d39") === -1) {
                const updatedLabels = [
                  ...jobData.idLabels,
                  "6285ea079131e28982c19d39",
                ];
                await retryFirebaseUpdate(() =>
                  updateDoc(doc(db, "JobData", e.draggableId), {
                    idLabels: updatedLabels,
                  })
                );
              }
            }
          }

          if (
            e.destination.droppableId === "bbb73c17-6690-4d43-8569-be12b0d5ee27"
          ) {
            const docSnap = await getDoc(doc(db, "JobData", e.draggableId));
            if (docSnap.exists()) {
              const jobData = docSnap.data();
              if (jobData.idLabels.indexOf("5e6881d6af988c41f2bf6e14") === -1) {
                const updatedLabels = [
                  ...jobData.idLabels,
                  "5e6881d6af988c41f2bf6e14",
                ];
                await retryFirebaseUpdate(() =>
                  updateDoc(doc(db, "JobData", e.draggableId), {
                    idLabels: updatedLabels,
                  })
                );
              }
            }
          }
        } catch (jobUpdateError) {
          console.error("Failed to update job properties:", jobUpdateError);
          // Job property update failure shouldn't rollback the list move
        }
      }
    } catch (error) {
      console.error("Failed to update lists in Firebase:", error);

      // Show user feedback about the failure
      alert(
        "Failed to move item. Please try again. If the problem persists, refresh the page."
      );
    }
  }

  useEffect(() => {
    window.addEventListener("error", (e) => {
      if (e.message === "ResizeObserver loop limit exceeded") {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div"
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay"
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    });
  }, []);

  const height = use100vh();

  useEffect(() => {
    const jobID = urlQuery.get("jobID");
    const clientID = urlQuery.get("clientID");

    if (jobID && clientID) {
      setModalJobData({ id: jobID });
      setModalClientData({ id: clientID });
      setModalOpen(true);
    } else {
      setModalOpen(false);
      setModalJobData({});
      setModalClientData({});
    }
  }, [urlQuery]);


  const updateRecentlyAssignedStaff = async (staffId) => {
    let updatedList = [
      staffId,
      ...recentlyAssignedStaff.filter((id) => id !== staffId),
    ];
    if (updatedList.length > 5) updatedList = updatedList.slice(0, 5);

    setRecentlyAssignedStaff(updatedList);

    await setDoc(
      doc(db, "AdminCollection", "recentlyAssignedStaff"),
      { recentlyAssignedStaff: updatedList },
      { merge: true }
    );
  };

  const checkInvoiceStatusList = async (list) => {
    const listItem = lists.find((item) => item.id === list?.id);
    if (!listItem) {
      console.log("No item found with the specified ID");
      return;
    }

    const itemsToProcess = listItem.items; //slice(-500);

    for (const item of itemsToProcess) {
      await checkInvoiceStatus(item, {}, lists, false);
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    console.log("Processed items:", itemsToProcess);
  };

  return (
    <>
      <GlobalStyles />
      <Modal
        open={eventDataModalV2Open}
        onClose={() => {
          setEventDataModalV2Open(false);
        }}
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          paddingTop: isMobile ? 0 : 10,
          paddingLeft: 0,
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
        }}
      >
        <Slide
          direction={"up"}
          in={eventDataModalV2Open}
          mountOnEnter
          unmountOnExit
          timeout={120}
        >
          <div
            style={{
              width: (() => {
                if (isMobile) return "100vw";
                return "800px";
              })(),
              height: (() => {
                if (isMobile) return "100vh";
                return `calc(100vh - 125px)`;
              })(),
              background:
                theme === "dark" ? "rgb(28, 28, 28)" : "rgb(250, 250, 250)",
              zIndex: 1100,
              position: "relative",
              outline: "none",
              boxShadow: "0px 3px 10px rgba(0, 0, 0, 0.2)",
              overflowY: "auto",
              borderRadius: (() => {
                if (isMobile) return 0;
                return "10px";
              })(),
            }}
          >
            <div>
              <EventDataModalV2
                initialJobData={modalJobData}
                eventDataModalOpen={eventDataModalV2Open}
                setEventDataModalOpen={setEventDataModalV2Open}
              />
            </div>
          </div>
        </Slide>
      </Modal>

      <div
        id="board"
        style={{
          overflowY: "hidden",
        }}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          {lists[0]?.items &&
            lists?.map((list, listIndex) => (
              <React.Fragment key={list.id}>
                {list?.items?.length > 50 ? (
                  <VirtualList
                    key={list.id}
                    list={list}
                    height={height}
                    clientData={clientData}
                    setModalOpen={setModalOpen}
                    setModalJobData={setModalJobData}
                    setModalClientData={setModalClientData}
                    recentlyAssignedStaff={recentlyAssignedStaff}
                    updateRecentlyAssignedStaff={updateRecentlyAssignedStaff}
                    checkInvoiceStatusList={checkInvoiceStatusList}
                    setEventDataModalV2Open={setEventDataModalV2Open}
                    isAdmin={isAdmin}
                    editMode={isAdmin && editMode}
                    setEditMode={setEditMode}
                    listIndex={listIndex}
                    totalLists={lists.length}
                    onMoveList={moveList}
                    onDeleteList={deleteList}
                    onRenameList={renameList}
                    onAddList={addList}
                  />
                ) : (
                  <List
                    key={list.id}
                    user={user}
                    list={list}
                    height={height}
                    clientData={clientData}
                    setModalOpen={setModalOpen}
                    setModalJobData={setModalJobData}
                    setModalClientData={setModalClientData}
                    recentlyAssignedStaff={recentlyAssignedStaff}
                    updateRecentlyAssignedStaff={updateRecentlyAssignedStaff}
                    checkInvoiceStatusList={checkInvoiceStatusList}
                    setEventDataModalV2Open={setEventDataModalV2Open}
                    isAdmin={isAdmin}
                    editMode={isAdmin && editMode}
                    setEditMode={setEditMode}
                    listIndex={listIndex}
                    totalLists={lists.length}
                    onMoveList={moveList}
                    onDeleteList={deleteList}
                    onRenameList={renameList}
                    onAddList={addList}
                  />
                )}
              </React.Fragment>
            ))}
        </DragDropContext>
      </div>
    </>
  );
}
export default Kanban;

const List = ({
  user,
  list,
  clientData,
  setModalOpen,
  setModalJobData,
  setModalClientData,
  recentlyAssignedStaff,
  updateRecentlyAssignedStaff,
  checkInvoiceStatusList,
  setEventDataModalV2Open,
  isAdmin,
  editMode,
  setEditMode,
  listIndex,
  totalLists,
  onMoveList,
  onDeleteList,
  onRenameList,
  onAddList,
}) => {
  const [theme] = useAtom(themeAtom);
  const [lists, setLists] = useAtom(listsAtom);
  const [labelColours] = useAtom(labelColoursAtom);
  const [listBackgroundColour, setlistBackgroundColour] = useState(
    theme === "dark" ? "#101010" : "#e0e0e0"
  );
  const [isCheckingInvoiceStatus, setIsCheckingInvoiceStatus] = useState(false);

  const [listOptionsMenuOpen, setListOptionsMenuOpen] = useState(false);
  const listOptionsMenuAnchor = useRef(null);

  const [newJobObject] = useAtom(newJobObjectAtom);
  const [currentStaffData] = useAtom(currentStaffDataAtom);

  const handleListOptionsClick = (event) => {
    setListOptionsMenuOpen(true);
  };

  const handleListOptionsClose = () => {
    setListOptionsMenuOpen(false);
  };

  useEffect(() => {
    const main = async () => {
      const parseListBackgroundColour = (days) => {
        if (days === 0) return theme === "dark" ? "#101010" : "#f5f5f5";
        try {
          if (days === 1)
            return labelColours
              ? labelColours["sky"][theme]
              : theme === "dark"
              ? "#376972"
              : "#7BC4D0";
          if (days === 2)
            return labelColours
              ? labelColours["yellow"][theme]
              : theme === "dark"
              ? "#7A601D"
              : "#dbce3d";
          if (days >= 3)
            return labelColours
              ? labelColours["red"][theme]
              : theme === "dark"
              ? "#A03624"
              : "#d44e3f";
        } catch (e) {
          return theme === "dark" ? "#101010" : "#f5f5f5";
        }
        return theme === "dark" ? "#101010" : "#f5f5f5";
      };

      if (list.id !== "5f081763cd97556d03075c86") {
        setlistBackgroundColour(theme === "dark" ? "#101010" : "#f5f5f5");
        return;
      }
      let highest = 0;
      for (let i = 0; i < list.items.length; i++) {
        const job = await getDoc(doc(db, "JobData", list.items[i]));
        if (!job.exists()) continue;

        const jobData = job.data();
        if (!jobData?.isInShootingTime) continue;

        const difference = getDaysDifference(
          jobData.isInShootingTime,
          DateTime.now()
            .setZone("Australia/Sydney")
            .startOf("day")
            .toUTC()
            .toISO()
        );
        if (difference > highest) highest = difference;
      }
      setlistBackgroundColour(parseListBackgroundColour(highest));
    };
    main();
    const interval = setInterval(main, 60000 * 60);
    return () => {
      clearInterval(interval);
    };
  }, [list, theme, labelColours]);

  const menuItemStyle = {
    fontSize: 14,
  };
  const sortByDueDate = async () => {
    handleListOptionsClose();

    try {
      const listObj = [];
      for (let i = 0; i < list.items.length; i++) {
        const job = await getDoc(doc(db, "JobData", list.items[i]));
        const unix = DateTime.fromISO(job.data().dueDate)
          .setZone("Australia/Sydney")
          .valueOf();
        listObj.push({ id: list.items[i], dd: unix });
      }
      listObj.sort((a, b) => a.dd - b.dd);
      const sortedItems = listObj.map((li) => li.id);

      // Create immutable copy of lists
      const listIndex = lists.findIndex((l) => l.id === list.id);
      if (listIndex === -1) {
        console.error("Cannot find list to sort");
        return;
      }

      const newLists = [...lists];
      newLists[listIndex] = {
        ...newLists[listIndex],
        items: sortedItems,
      };

      setLists(newLists);
      await retryFirebaseUpdate(() =>
        updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
      );
      console.log("Successfully sorted list by due date");
    } catch (error) {
      console.error("Failed to sort by due date:", error);
      alert("Failed to sort list. Please try again.");
    }
  };

  const sortByJobNumber = async () => {
    handleListOptionsClose();

    try {
      const listObj = [];
      for (let i = 0; i < list.items.length; i++) {
        const job = await getDoc(doc(db, "JobData", list.items[i]));
        listObj.push({ id: list.items[i], jobNumber: job.data().jobNumber });
      }
      listObj.sort((a, b) => a.jobNumber - b.jobNumber);
      const sortedItems = listObj.map((li) => li.id);

      // Create immutable copy of lists
      const listIndex = lists.findIndex((l) => l.id === list.id);
      if (listIndex === -1) {
        console.error("Cannot find list to sort");
        return;
      }

      const newLists = [...lists];
      newLists[listIndex] = {
        ...newLists[listIndex],
        items: sortedItems,
      };

      setLists(newLists);
      await retryFirebaseUpdate(() =>
        updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
      );
      console.log("Successfully sorted list by job number");
    } catch (error) {
      console.error("Failed to sort by job number:", error);
      alert("Failed to sort list. Please try again.");
    }
  };

  const [newPostJobModalOpen, setNewPostJobModalOpen] = useState(false);

  const modalStyle = () => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "95vw", sm: 600 },
    maxHeight: "90vh",
    backgroundColor:
      theme === "dark" ? "rgba(28, 28, 28, 0.95)" : "rgba(250, 250, 250, 0.95)",
    backdropFilter: "blur(10px)",
    outline: "none",
    boxShadow:
      theme === "dark"
        ? "0 8px 32px rgba(0,0,0,0.4)"
        : "0 8px 32px rgba(0,0,0,0.15)",
    borderRadius: "8px",
    border:
      theme === "dark"
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  });

  const [newPostJobErrorDialogOpen, setNewPostJobErrorDialogOpen] =
    useState(false);
  const handleNewPostJobErrorDialogClose = () =>
    setNewPostJobErrorDialogOpen(false);

  const [newPostJobJobNumber, setNewPostJobJobNumber] = useState("");
  const [newPostJobJobType, setNewPostJobJobType] = useState("");
  const [newPostJobMultipleDays, setNewPostJobMultipleDays] = useState("");
  const [newPostJobClient, setNewPostJobClient] = useState("");
  const [newPostJobExpress, setNewPostJobExpress] = useState(false);
  const [newPostJobDueDate, setNewPostJobDueDate] = useState(null);
  const [newPostJobExpressDueDate, setNewPostJobExpressDueDate] =
    useState(null);
  const [
    newPostJobInvoicingSpecialRequirements,
    setNewPostJobInvoicingSpecialRequirements,
  ] = useState("");
  const [
    newPostJobRetouchingSpecialRequirements,
    setNewPostJobRetouchingSpecialRequirements,
  ] = useState("");

  const addNewPostJob = async () => {
    let shouldContinue = true;
    if (!newPostJobJobNumber) shouldContinue = false;
    if (!newPostJobJobType) shouldContinue = false;
    if (!newPostJobClient) shouldContinue = false;
    if (!newPostJobDueDate) shouldContinue = false;

    if (!shouldContinue) {
      setNewPostJobErrorDialogOpen(true);
      return;
    }
    const jts = newPostJobJobType.match(/^.*\((-.*)\)/)[1];
    const jn = `${newPostJobJobNumber}-${newPostJobClient}${
      newPostJobMultipleDays ? `-${newPostJobMultipleDays}` : ""
    }${jts}`;

    const newJobInfo = {
      ...newJobObject,
      client: newPostJobClient,
      jobNumber: parseInt(newPostJobJobNumber),
      multipleDays: newPostJobMultipleDays,
      jobName: jn,
      express: newPostJobExpress,
      dueDate: newPostJobDueDate,
      expressDueDate: newPostJobExpressDueDate,
      invoicingSpecialRequirements: newPostJobInvoicingSpecialRequirements,
      retouchingSpecialRequirements: newPostJobRetouchingSpecialRequirements,
      allDay: false,
      end: "",
      start: "",
      shootDate: null,
      bookedStart: null,
      bookedFinish: null,
      studio: "",
      year: new Date().getFullYear(),
      jobTypeFull: newPostJobJobType,
      jobTypeShort: newPostJobJobType.match(/^.*\((-.*)\)/)[1],
      imageBreakdown: [
        {
          id: uuidv4(),
          description: "File Count",
          jobTypesFilter: [],
          expectedPerHour: "",
          costPerFile: "",
          existingItem: true,
        },
      ],
      checklists: [
        {
          name: "Colour Corrections",
          id: uuidv4(),
          checklistItems: [],
        },
        {
          name: "Colour Changes",
          id: uuidv4(),
          checklistItems: [],
        },
        {
          name: "Retouching Notes",
          id: uuidv4(),
          checklistItems: [],
        },
      ],
    };
    try {
      const docRef = await addDoc(collection(db, "JobData"), newJobInfo);

      // Create immutable copy of lists
      const listIndex = lists.findIndex((l) => l.id === list.id);
      if (listIndex === -1) {
        console.error("Cannot find list to add new job");
        return;
      }

      const newLists = [...lists];
      newLists[listIndex] = {
        ...newLists[listIndex],
        items: [...new Set([...newLists[listIndex].items, docRef.id])],
      };

      // Update lists state and Firebase
      setLists(newLists);
      await retryFirebaseUpdate(() =>
        updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
      );

      // Close modal and reset form
      setNewPostJobModalOpen(false);
      setNewPostJobJobNumber("");
      setNewPostJobJobType("");
      setNewPostJobMultipleDays("");
      setNewPostJobClient("");
      setNewPostJobExpress(false);
      setNewPostJobDueDate(null);
      setNewPostJobExpressDueDate(null);

      console.log("Successfully created new post job:", docRef.id);
    } catch (error) {
      console.error("Failed to create new post job:", error);
      alert("Failed to create new job. Please try again.");
    }
  };

  return (
    <>
      <Dialog
        open={newPostJobErrorDialogOpen}
        onClose={handleNewPostJobErrorDialogClose}
      >
        <DialogContent>
          <DialogContentText>
            Please ensure the following fields are filled out:
          </DialogContentText>
          <DialogContentText>Client</DialogContentText>
          <DialogContentText>Due Date</DialogContentText>
          <DialogContentText>Job Number</DialogContentText>
          <DialogContentText>Job Type</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNewPostJobErrorDialogClose} autoFocus>
            Okay
          </Button>
        </DialogActions>
      </Dialog>
      <Modal
        open={newPostJobModalOpen}
        onClose={() => {
          setNewPostJobModalOpen(false);
        }}
        sx={{
          "& > .MuiBackdrop-root": {
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
        >
          <Paper elevation={0} sx={modalStyle()}>
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 100,
                backgroundColor:
                  theme === "dark"
                    ? "rgba(28, 28, 28, 0.95)"
                    : "rgba(250, 250, 250, 0.95)",
                backdropFilter: "blur(10px)",
                borderBottom: `1px solid ${
                  theme === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.1)"
                }`,
                padding: "16px 24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  color: theme === "dark" ? "#ffffff" : "#1a202c",
                  margin: 0,
                }}
              >
                New Post Job
              </Typography>
              <IconButton
                onClick={() => setNewPostJobModalOpen(false)}
                sx={{
                  padding: "8px",
                  borderRadius: "6px",
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.05)",
                  "&:hover": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.1)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: "1.2rem" }} />
              </IconButton>
            </Box>

            {/* Content */}
            <Box
              sx={{
                padding: "24px",
                overflow: "auto",
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <KanbanClientSelector
                clientData={clientData}
                newPostJobClient={newPostJobClient}
                setNewPostJobClient={setNewPostJobClient}
                newPostJobDueDate={newPostJobDueDate}
                setNewPostJobDueDate={setNewPostJobDueDate}
              />
              <KanbanDueDateExpressContainer
                newPostJobDueDate={newPostJobDueDate}
                setNewPostJobDueDate={setNewPostJobDueDate}
                newPostJobExpress={newPostJobExpress}
                setNewPostJobExpress={setNewPostJobExpress}
                newPostJobExpressDueDate={newPostJobExpressDueDate}
                setNewPostJobExpressDueDate={setNewPostJobExpressDueDate}
              />
              <KanbanJobNumber
                newPostJobJobNumber={newPostJobJobNumber}
                setNewPostJobJobNumber={setNewPostJobJobNumber}
              />
              <KanbanJobTypeSelector
                newPostJobJobType={newPostJobJobType}
                setNewPostJobJobType={setNewPostJobJobType}
                newPostJobMultipleDays={newPostJobMultipleDays}
                setNewPostJobMultipleDays={setNewPostJobMultipleDays}
              />
              <KanbanSpecialRequirementsContainer
                value={newPostJobInvoicingSpecialRequirements}
                setValue={setNewPostJobInvoicingSpecialRequirements}
                label="Invoicing Special Requirements"
              />
              <KanbanSpecialRequirementsContainer
                value={newPostJobRetouchingSpecialRequirements}
                setValue={setNewPostJobRetouchingSpecialRequirements}
                label="Retouching Special Requirements"
              />
              <Button
                variant="contained"
                onClick={addNewPostJob}
                sx={{
                  marginTop: 2,
                  alignSelf: "flex-start",
                  borderRadius: "6px",
                  padding: "12px 24px",
                  fontWeight: "bold",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Create Job
              </Button>
            </Box>
          </Paper>
        </motion.div>
      </Modal>
      <Menu
        anchorEl={listOptionsMenuAnchor?.current}
        keepMounted
        open={listOptionsMenuOpen}
        onClose={handleListOptionsClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        disableScrollLock={true}
        PaperProps={{
          sx: {
            width: 215,
            background:
              theme === "dark"
                ? "rgba(35, 35, 35, 0.95)"
                : "rgba(248, 250, 252, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: "6px",
            boxShadow:
              theme === "dark"
                ? "0 8px 32px rgba(0,0,0,0.4)"
                : "0 8px 32px rgba(0,0,0,0.15)",
            border:
              theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <div style={{ paddingTop: 2, paddingLeft: 15, paddingRight: 15 }}>
          <span
            style={{ fontWeight: "bold", marginBottom: 10, display: "block" }}
          >
            Sort By:
          </span>
          <Divider sx={{ margin: 1 }} />
          <MenuItem sx={menuItemStyle} onClick={sortByDueDate}>
            Due Date
          </MenuItem>
          <Divider />
          <MenuItem sx={menuItemStyle} onClick={sortByJobNumber}>
            Job Number
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={async () => {
              setIsCheckingInvoiceStatus(true);
              try {
                await checkInvoiceStatusList(list);
              } catch (error) {
                console.error(
                  "Error checking invoice status for list:",
                  list?.id,
                  error
                );
              } finally {
                setIsCheckingInvoiceStatus(false);
              }
            }}
            disabled={isCheckingInvoiceStatus}
            sx={{
              ...menuItemStyle,
              "@keyframes pulseTextColorList": {
                "0%": {
                  color: theme === "dark" ? "#ffffff" : "#000000",
                  opacity: 1,
                },
                "50%": {
                  color: theme === "dark" ? "#666666" : "#777777",
                  opacity: 0.7,
                },
                "100%": {
                  color: theme === "dark" ? "#ffffff" : "#000000",
                  opacity: 1,
                },
              },
              ...(isCheckingInvoiceStatus && {
                animation: "pulseTextColorList 1.5s infinite ease-in-out",
              }),
            }}
          >
            Check Invoice Status
          </MenuItem>
          {isAdmin ? (
            <>
              <Divider />
              <MenuItem
                sx={menuItemStyle}
                onClick={() => {
                  handleListOptionsClose();
                  onAddList(list.id);
                }}
              >
                Add List To Right
              </MenuItem>
              <Divider />
              <MenuItem
                sx={menuItemStyle}
                onClick={() => {
                  handleListOptionsClose();
                  setEditMode((v) => !v);
                }}
              >
                {editMode ? "Done Editing Lists" : "Edit Lists"}
              </MenuItem>
            </>
          ) : null}
          <div style={{ margin: 5 }}></div>
        </div>
      </Menu>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="list-wrapper"
        style={{ position: "relative" }}
      >
        <Paper
          elevation={2}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            backgroundColor:
              theme === "dark"
                ? "rgba(35, 35, 35, 0.95)"
                : "rgba(248, 250, 252, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: "6px 6px 0px 0px !important",
            padding: "6px 10px 4px 10px",
            marginBottom: 0,
            boxShadow:
              theme === "dark"
                ? "0 4px 20px rgba(0,0,0,0.3)"
                : "0 4px 20px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "0.9rem",
                color: theme === "dark" ? "#ffffff" : "#1a202c",
                margin: 0,
                userSelect: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              className="list-header"
            >
              {list.name}
            </Typography>
            <Box
              ref={listOptionsMenuAnchor}
              sx={{
                display: "flex",
                alignItems: "center",
                transition: "all 0.2s ease",
              }}
            >
              <Chip
                label={list.items.length}
                size="small"
                onClick={handleListOptionsClick}
                sx={{
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(144, 202, 249, 0.15)"
                      : "rgba(25, 118, 210, 0.1)",
                  color: theme === "dark" ? "#90caf9" : "#1976d2",
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  height: "28px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  padding: "0 10px",
                  minWidth: "28px",
                  "&:hover": {
                    backgroundColor:
                      theme === "dark"
                        ? "rgba(144, 202, 249, 0.25)"
                        : "rgba(25, 118, 210, 0.2)",
                  },
                }}
              />
            </Box>
          </Box>
          {editMode ? (
            <ListEditControls
              list={list}
              index={listIndex}
              totalLists={totalLists}
              onMove={onMoveList}
              onDelete={onDeleteList}
              onRename={onRenameList}
            />
          ) : null}
        </Paper>
        <Box
          sx={{
            backgroundColor: listBackgroundColour,
            borderRadius: "0 0 6px 6px",
            overflow: "visible",
            boxShadow:
              theme === "dark"
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.08)",
          }}
        >
          <Droppable
            droppableId={list.id}
            style={{ overflowY: "scroll" }}
            renderClone={(provided, snapshot, rubric) => (
              <Card
                provided={provided}
                isDragging={snapshot.isDragging}
                item={list.items[rubric.source.index]}
                items={list}
                clientData={clientData}
                clone={false}
                setModalOpen={setModalOpen}
                setModalJobData={setModalJobData}
                setModalClientData={setModalClientData}
                listId={list.id}
                recentlyAssignedStaff={recentlyAssignedStaff}
                updateRecentlyAssignedStaff={updateRecentlyAssignedStaff}
                setEventDataModalV2Open={setEventDataModalV2Open}
              />
            )}
          >
            {(provided) => {
              return (
                <div
                  className="list"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  <div style={{ width: "100%", height: 5 }}></div>
                  <div className="list-cards">
                    {list.items.map((item, index) => (
                      <Draggable draggableId={item} index={index} key={item}>
                        {(provided, snapshot) => {
                          // Error-handling test throw — re-enable to exercise the error boundary
                          return (<div style={{ paddingLeft: 6, paddingRight: 6 }}>
                            <Card
                              provided={provided}
                              isDragging={snapshot.isDragging}
                              item={item}
                              items={list.items}
                              clientData={clientData}
                              clone={false}
                              setModalOpen={setModalOpen}
                              setModalJobData={setModalJobData}
                              setModalClientData={setModalClientData}
                              listId={list.id}
                              recentlyAssignedStaff={recentlyAssignedStaff}
                              updateRecentlyAssignedStaff={
                                updateRecentlyAssignedStaff
                              }
                              setEventDataModalV2Open={setEventDataModalV2Open}
                            />
                          </div>
                          );
                        }}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    <div style={{ width: "100%", height: 10 }}></div>
                  </div>
                </div>
              );
            }}
          </Droppable>
          {list.id === "5f08176c00570287a11c2272" ||
          list.id === "a68239ba-193d-4058-98f3-cdd4b2842efd" ? (
            <div style={{ padding: "0px 0px 6px 0px" }}>
              <motion.div
                className="addNewPostCard"
                whileHover={{
                  scale: 1.02,
                  backgroundColor: theme === "dark" ? "#2a2a2a" : "#e8e8e8",
                }}
                whileTap={{ scale: 0.98 }}
                style={{
                  backgroundColor: theme === "dark" ? "#272727" : "#ebebeb",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  padding: 8,
                  borderRadius: 6,
                  transition: "all 0.2s ease",
                  boxShadow:
                    theme === "dark"
                      ? "0 2px 8px rgba(0,0,0,0.3)"
                      : "0 2px 8px rgba(0,0,0,0.1)",
                }}
                onClick={() => {
                  if (user) {
                    setNewPostJobModalOpen(true);
                  } else {
                    alert("login in to make post card");
                  }
                }}
              >
                <AddIcon
                  sx={{
                    marginRight: 1,
                    fontSize: 20,
                    color: theme === "dark" ? "#90caf9" : "#1976d2",
                  }}
                />
                <Typography
                  sx={{
                    color: theme === "dark" ? "#ffffff" : "#1a202c",
                    fontWeight: 500,
                    fontSize: "0.95rem",
                  }}
                >
                  New Post Card
                </Typography>
              </motion.div>
            </div>
          ) : null}
        </Box>
      </motion.div>
    </>
  );
};

const VirtualList = ({
  list,
  clientData,
  setModalOpen,
  setModalJobData,
  setModalClientData,
  recentlyAssignedStaff,
  updateRecentlyAssignedStaff,
  checkInvoiceStatusList,
  setEventDataModalV2Open,
  isAdmin,
  editMode,
  setEditMode,
  listIndex,
  totalLists,
  onMoveList,
  onDeleteList,
  onRenameList,
  onAddList,
}) => {
  const [theme] = useAtom(themeAtom);
  const [lists, setLists] = useAtom(listsAtom);
  const [labelColours] = useAtom(labelColoursAtom);
  const [listBackgroundColour, setlistBackgroundColour] = useState(
    theme === "dark" ? "#101010" : "#e0e0e0"
  );
  const [listOptionsMenuOpen, setListOptionsMenuOpen] = useState(false);
  const listOptionsMenuAnchor = useRef(null);
  const [isCheckingInvoiceStatus, setIsCheckingInvoiceStatus] = useState(false);

  useEffect(() => {
    const main = async () => {
      const parseListBackgroundColour = (days) => {
        if (days === 0) return theme === "dark" ? "#101010" : "#f5f5f5";
        try {
          if (days === 1)
            return labelColours
              ? labelColours["sky"][theme]
              : theme === "dark"
              ? "#376972"
              : "#7BC4D0";
          if (days === 2)
            return labelColours
              ? labelColours["yellow"][theme]
              : theme === "dark"
              ? "#7A601D"
              : "#dbce3d";
          if (days >= 3)
            return labelColours
              ? labelColours["red"][theme]
              : theme === "dark"
              ? "#A03624"
              : "#d44e3f";
        } catch (e) {
          return theme === "dark" ? "#101010" : "#f5f5f5";
        }
        return theme === "dark" ? "#101010" : "#f5f5f5";
      };

      if (list.id !== "5f081763cd97556d03075c86") {
        setlistBackgroundColour(theme === "dark" ? "#101010" : "#e0e0e0");
        return;
      }
      let highest = 0;
      for (let i = 0; i < list.items.length; i++) {
        const job = await getDoc(doc(db, "JobData", list.items[i]));
        if (!job.exists()) continue;

        const jobData = job.data();
        if (!jobData?.isInShootingTime) continue;

        const difference = getDaysDifference(
          jobData.isInShootingTime,
          DateTime.now()
            .setZone("Australia/Sydney")
            .startOf("day")
            .toUTC()
            .toISO()
        );
        if (difference > highest) highest = difference;
      }
      setlistBackgroundColour(parseListBackgroundColour(highest));
    };
    main();
    const interval = setInterval(main, 60000 * 60);
    return () => {
      clearInterval(interval);
    };
  }, [list, theme, labelColours]);

  const handleListOptionsClick = (event) => {
    setListOptionsMenuOpen(true);
  };

  const handleListOptionsClose = () => {
    setListOptionsMenuOpen(false);
  };

  const menuItemStyle = {
    fontSize: 14,
  };

  const sortByDueDate = async () => {
    handleListOptionsClose();

    try {
      const listObj = [];
      for (let i = 0; i < list.items.length; i++) {
        const job = await getDoc(doc(db, "JobData", list.items[i]));
        const unix = DateTime.fromISO(job.data().dueDate)
          .setZone("Australia/Sydney")
          .valueOf();
        listObj.push({ id: list.items[i], dd: unix });
      }
      listObj.sort((a, b) => a.dd - b.dd);
      const sortedItems = listObj.map((li) => li.id);

      // Create immutable copy of lists
      const listIndex = lists.findIndex((l) => l.id === list.id);
      if (listIndex === -1) {
        console.error("Cannot find list to sort");
        return;
      }

      const newLists = [...lists];
      newLists[listIndex] = {
        ...newLists[listIndex],
        items: sortedItems,
      };

      setLists(newLists);
      await retryFirebaseUpdate(() =>
        updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
      );
      console.log("Successfully sorted virtual list by due date");
    } catch (error) {
      console.error("Failed to sort virtual list by due date:", error);
      alert("Failed to sort list. Please try again.");
    }
  };

  const sortByJobNumber = async () => {
    handleListOptionsClose();

    try {
      const listObj = [];
      for (let i = 0; i < list.items.length; i++) {
        const job = await getDoc(doc(db, "JobData", list.items[i]));
        listObj.push({ id: list.items[i], jobNumber: job.data().jobNumber });
      }
      listObj.sort((a, b) => a.jobNumber - b.jobNumber);
      const sortedItems = listObj.map((li) => li.id);

      // Create immutable copy of lists
      const listIndex = lists.findIndex((l) => l.id === list.id);
      if (listIndex === -1) {
        console.error("Cannot find list to sort");
        return;
      }

      const newLists = [...lists];
      newLists[listIndex] = {
        ...newLists[listIndex],
        items: sortedItems,
      };

      setLists(newLists);
      await retryFirebaseUpdate(() =>
        updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
      );
      console.log("Successfully sorted virtual list by job number");
    } catch (error) {
      console.error("Failed to sort virtual list by job number:", error);
      alert("Failed to sort list. Please try again.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="list-wrapper-virtual"
      style={{ position: "relative", display: "flex", flexDirection: "column" }}
    >
      <Paper
        elevation={2}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          backgroundColor:
            theme === "dark"
              ? "rgba(35, 35, 35, 0.95)"
              : "rgba(248, 250, 252, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: "6px 6px 0px 0px !important",
          padding: "6px 10px 4px 10px",
          marginBottom: 0,
          boxShadow:
            theme === "dark"
              ? "0 4px 20px rgba(0,0,0,0.3)"
              : "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              fontSize: "0.9rem",
              color: theme === "dark" ? "#ffffff" : "#1a202c",
              margin: 0,
              userSelect: "none",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            className="list-header"
          >
            {list.name}
          </Typography>
          <Box
            ref={listOptionsMenuAnchor}
            sx={{
              display: "flex",
              alignItems: "center",
              transition: "all 0.2s ease",
            }}
          >
            <Chip
              label={list.items.length}
              size="small"
              onClick={handleListOptionsClick}
              sx={{
                backgroundColor:
                  theme === "dark"
                    ? "rgba(144, 202, 249, 0.15)"
                    : "rgba(25, 118, 210, 0.1)",
                color: theme === "dark" ? "#90caf9" : "#1976d2",
                fontWeight: "bold",
                fontSize: "0.85rem",
                height: "28px",
                borderRadius: "8px",
                cursor: "pointer",
                padding: "0 10px",
                minWidth: "28px",
                "&:hover": {
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(144, 202, 249, 0.25)"
                      : "rgba(25, 118, 210, 0.2)",
                },
              }}
            />
          </Box>
        </Box>
        {editMode ? (
          <ListEditControls
            list={list}
            index={listIndex}
            totalLists={totalLists}
            onMove={onMoveList}
            onDelete={onDeleteList}
            onRename={onRenameList}
          />
        ) : null}
      </Paper>
      <Menu
        anchorEl={listOptionsMenuAnchor?.current}
        keepMounted
        open={listOptionsMenuOpen}
        onClose={handleListOptionsClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        disableScrollLock={true}
        PaperProps={{
          sx: {
            width: 200,
            background:
              theme === "dark"
                ? "rgba(35, 35, 35, 0.95)"
                : "rgba(248, 250, 252, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: "6px",
            boxShadow:
              theme === "dark"
                ? "0 8px 32px rgba(0,0,0,0.4)"
                : "0 8px 32px rgba(0,0,0,0.15)",
            border:
              theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <div style={{ paddingTop: 2, paddingLeft: 15, paddingRight: 15 }}>
          <span
            style={{ fontWeight: "bold", marginBottom: 10, display: "block" }}
          >
            Sort By:
          </span>
          <Divider sx={{ margin: 1 }} />
          <MenuItem sx={menuItemStyle} onClick={sortByDueDate}>
            Due Date
          </MenuItem>
          <Divider />
          <MenuItem sx={menuItemStyle} onClick={sortByJobNumber}>
            Job Number
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={async () => {
              // Don't close the menu - we want to see the pulsing animation
              setIsCheckingInvoiceStatus(true);
              try {
                await checkInvoiceStatusList(list);
              } catch (error) {
                console.error(
                  "Error checking invoice status for list:",
                  list?.id,
                  error
                );
              } finally {
                setIsCheckingInvoiceStatus(false);
              }
            }}
            disabled={isCheckingInvoiceStatus}
            sx={{
              ...menuItemStyle,
              "@keyframes pulseTextColorVirtualList": {
                "0%": {
                  color: theme === "dark" ? "#ffffff" : "#000000",
                  opacity: 1,
                },
                "50%": {
                  color: theme === "dark" ? "#666666" : "#777777",
                  opacity: 0.7,
                },
                "100%": {
                  color: theme === "dark" ? "#ffffff" : "#000000",
                  opacity: 1,
                },
              },
              ...(isCheckingInvoiceStatus && {
                animation:
                  "pulseTextColorVirtualList 1.5s infinite ease-in-out",
              }),
            }}
          >
            Check Invoice Status
          </MenuItem>
          {isAdmin ? (
            <>
              <Divider />
              <MenuItem
                sx={menuItemStyle}
                onClick={() => {
                  handleListOptionsClose();
                  onAddList(list.id);
                }}
              >
                Add List To Right
              </MenuItem>
              <Divider />
              <MenuItem
                sx={menuItemStyle}
                onClick={() => {
                  handleListOptionsClose();
                  setEditMode((v) => !v);
                }}
              >
                {editMode ? "Done Editing Lists" : "Edit Lists"}
              </MenuItem>
            </>
          ) : null}
          <div style={{ margin: 5 }}></div>
        </div>
      </Menu>
      <Box
        sx={{
          backgroundColor: listBackgroundColour,
          borderRadius: "0 0 6px 6px",
          overflow: "visible",
          boxShadow:
            theme === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.08)",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="list-virtual" style={{ flexGrow: 1 }}>
          <Droppable
            droppableId={list.id}
            mode="virtual"
            renderClone={(provided, snapshot, rubric) => (
              <Card
                provided={provided}
                isDragging={snapshot.isDragging}
                item={list.items[rubric.source.index]}
                items={list}
                clientData={clientData}
                clone={false}
                setModalOpen={setModalOpen}
                setModalJobData={setModalJobData}
                setModalClientData={setModalClientData}
                listId={list.id}
                recentlyAssignedStaff={recentlyAssignedStaff}
                updateRecentlyAssignedStaff={updateRecentlyAssignedStaff}
                setEventDataModalV2Open={setEventDataModalV2Open}
              />
            )}
          >
            {(provided, snapshot) => {
              return (
                <Virtuoso
                  scrollerRef={provided.innerRef}
                  data={list.items}
                  className="list-cards"
                  style={{ flexGrow: 1 }}
                  itemContent={(index, item) => {
                    return (
                      <Draggable
                        draggableId={item}
                        index={index}
                        key={`${item}-${index}`}
                      >
                        {(provided) => (
                          <Card
                            key={`${item}-Card-${index}`}
                            provided={provided}
                            isDragging={snapshot.isDragging}
                            item={item}
                            items={list.items}
                            clientData={clientData}
                            clone={false}
                            setModalOpen={setModalOpen}
                            setModalJobData={setModalJobData}
                            setModalClientData={setModalClientData}
                            listId={list.id}
                            recentlyAssignedStaff={recentlyAssignedStaff}
                            updateRecentlyAssignedStaff={
                              updateRecentlyAssignedStaff
                            }
                            setEventDataModalV2Open={setEventDataModalV2Open}
                          />
                        )}
                      </Draggable>
                    );
                  }}
                />
              );
            }}
          </Droppable>
        </div>
      </Box>
    </motion.div>
  );
};

function Card({
  provided,
  item,
  isDragging,
  clientData,
  clone,
  setModalOpen,
  setModalJobData,
  setModalClientData,
  listId,
  recentlyAssignedStaff,
  updateRecentlyAssignedStaff,
  setEventDataModalV2Open,
}) {
  const [theme] = useAtom(themeAtom);
  const [labelColours] = useAtom(labelColoursAtom);
  const [lists, setLists] = useAtom(listsAtom);
  const [isAdmin] = useAtom(isAdminAtom);
  const [staffData] = useAtom(staffDataAtom);
  const [shouldPauseSubscriptions] = useAtom(shouldPauseSubscriptionsAtom);

  const [jobData, setJobData] = useState({});

  useEffect(() => {
    // Don't subscribe when subscriptions should be paused
    if (shouldPauseSubscriptions) {
      return;
    }

    let unsub;
    const main = () => {
      if (!item) return;
      unsub = onSnapshot(
        doc(db, "JobData", item),
        (docSnapshot) => {
          if (!docSnapshot.exists()) {
            setJobData({});
            return;
          }

          const snapshotData = docSnapshot.data();
          const data = snapshotData ? { ...snapshotData } : {};
          if (docSnapshot.id) {
            data.id = docSnapshot.id;
          }
          setJobData(data);
        },
        (error) => {
          console.log(error);
        }
      );
    };
    main();
    return () => {
      try {
        unsub();
      } catch (e) {}
    };
  }, [item, shouldPauseSubscriptions]);


  const [jobClientData, setJobClientData] = useState({});

  useEffect(() => {
    if (!jobData) return;
    if (!jobData.client) return;
    if (!clientData) return;
    try {
      clientData.forEach((c) => {
        if (c.brand === jobData.client) setJobClientData(c);
      });
    } catch (e) {
      return;
    }
  }, [jobData, clientData]);

  const [cardBackgroundColour, setCardBackgroundColour] = useState(
    theme === "dark" ? "#272727" : "#ebebeb"
  );

  const [jobTypes] = useAtom(jobTypesAtom);

  useEffect(() => {
    const specialListIds = [
      "5f08176c00570287a11c2272",
      "a68239ba-193d-4058-98f3-cdd4b2842efd",
    ];
    const isSpecialList = specialListIds.includes(listId);
    const isDarkTheme = theme === "dark";
    const hasOutsourcers = jobData?.outsourcers?.length > 0;
    const hasIdLabels = Array.isArray(jobData.idLabels);
    const hasSpecificLabel =
      hasIdLabels && jobData.idLabels.includes("6285ea079131e28982c19d39");

    let color;

    if (hasIdLabels) {
      if (hasSpecificLabel) {
        if (labelColours.length !== 0) {
          color = newShade(
            labelColours["black"][theme],
            isDarkTheme ? -20 : 40
          );
        } else {
          color = newShade(
            isDarkTheme ? "#5C6772" : "#8790A0",
            isDarkTheme ? -20 : 20
          );
        }
      } else {
        if (hasOutsourcers) {
          color = isDarkTheme ? "#272727" : "#ebebeb";
        } else if (isSpecialList) {
          color = isDarkTheme ? "#382c2c" : "#e0cece";
        } else {
          color = isDarkTheme ? "#272727" : "#ebebeb";
        }
      }
    } else {
      if (hasOutsourcers) {
        color = isDarkTheme ? "#272727" : "#ebebeb";
      } else if (isSpecialList) {
        color = isDarkTheme ? "#382c2c" : "#e0cece";
      } else {
        color = isDarkTheme ? "#272727" : "#ebebeb";
      }
    }

    const today = DateTime.now()
      .startOf("day")
      .setZone("Australia/Sydney")
      .toUTC()
      .toISO();

    const outstanding =
      jobData.outsourcers?.some((o) => {
        if (o.dueDate) {
          const iso = DateTime.fromISO(o.dueDate)
            .startOf("day")
            .setZone("Australia/Sydney")
            .toUTC()
            .toISO();
          return today < iso && specialListIds.includes(listId);
        }
        return false;
      }) || false;

    if (outstanding) {
      color = isDarkTheme ? "#2c3638" : "#cedde0";
    }

    if (listId === "bbb73c17-6690-4d43-8569-be12b0d5ee27") {
      const jobImageBreakdown = jobData?.imageBreakdown || [];
      const clientImageBreakdown = clientData?.imageBreakdown || [];
      const combinedImageBreakdown = [
        ...jobImageBreakdown,
        ...clientImageBreakdown,
      ];

      const filteredImageBreakdowns = combinedImageBreakdown.filter(
        (item) =>
          !item.itemHidden &&
          !item.quantitySupplied &&
          isJobTypeMatching(jobTypes, item.jobTypesFilter, jobData?.jobTypeFull)
      );

      if (filteredImageBreakdowns.length > 0) {
        color = isDarkTheme ? "#382c2c" : "#e0cece";
      }
    }

    setCardBackgroundColour(color);
  }, [theme, jobData, clientData, labelColours, listId, jobTypes]);

  const [contextMenu, setContextMenu] = useState(null);

  const handleContextMenu = (event) => {
    event.preventDefault();
    if (contextMenu) {
      setContextMenu(null);
    } else {
      setContextMenu({
        mouseX: event.clientX + 2,
        mouseY: event.clientY - 6,
      });
    }
  };

  const assignStaffMenuRef = useRef(null);
  const [assignStaffMenuOpen, setAssignStaffMenuOpen] = useState(false);

  const handleClose = () => {
    setContextMenu(null);
    setAssignStaffMenuOpen(false);
  };

  const toggleHold = async () => {
    handleClose();

    try {
      const holdID = "6285ea079131e28982c19d39";
      let updatedLabels;

      if (jobData?.idLabels.indexOf(holdID) !== -1) {
        updatedLabels = jobData.idLabels.filter((label) => label !== holdID);
      } else {
        updatedLabels = [...new Set([...jobData.idLabels, holdID])];
      }

      await retryFirebaseUpdate(() =>
        updateDoc(doc(db, "JobData", jobData.id), { idLabels: updatedLabels })
      );
      console.log("Successfully toggled hold status");
    } catch (error) {
      console.error("Failed to toggle hold status:", error);
      alert("Failed to toggle hold status. Please try again.");
    }
  };

  const deleteCard = async () => {
    handleClose();

    try {
      // Create immutable copy of lists with item removed
      const newLists = lists.map((list) => {
        if (list.items.includes(item)) {
          return {
            ...list,
            items: list.items.filter((listItem) => listItem !== item),
          };
        }
        return list;
      });

      setLists(newLists);
      await retryFirebaseUpdate(() =>
        updateDoc(doc(db, "AdminCollection", "lists"), { lists: newLists })
      );
      console.log("Successfully deleted card:", item);
    } catch (error) {
      console.error("Failed to delete card:", error);
      alert("Failed to delete card. Please try again.");
    }
  };

  return (
    <>
      <Menu
        open={Boolean(contextMenu)}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        PaperProps={{
          sx: {
            background:
              theme === "dark"
                ? "rgba(35, 35, 35, 0.95)"
                : "rgba(248, 250, 252, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: 6,
            boxShadow:
              theme === "dark"
                ? "0 8px 32px rgba(0,0,0,0.4)"
                : "0 8px 32px rgba(0,0,0,0.15)",
            border:
              theme === "dark"
                ? "1px solid rgba(255, 255, 255, 0.1)"
                : "1px solid rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setAssignStaffMenuOpen((prev) => !prev);
          }}
          ref={assignStaffMenuRef}
        >
          Assign Staff
          {assignStaffMenuOpen ? (
            <ExpandLess sx={{ marginLeft: 1 }} />
          ) : (
            <ExpandMore sx={{ marginLeft: 1 }} />
          )}
        </MenuItem>
        <Collapse in={assignStaffMenuOpen} timeout="auto" unmountOnExit>
          <Divider />
          <MUIList
            component="div"
            disablePadding
            sx={{
              maxHeight: 600,
              overflowY: "auto",
            }}
          >
            {/* Recently Assigned Staff Section */}
            {recentlyAssignedStaff.length > 0 && (
              <>
                {recentlyAssignedStaff.map((staffId, index) => {
                  const staff = staffData.find((sd) => sd.id === staffId);
                  return staff ? (
                    <ListItem disablePadding key={`recent_${index}`}>
                      <ListItemButton
                        onClick={async () => {
                          try {
                            if (!jobData.hasOwnProperty("assignedStaff"))
                              jobData.assignedStaff = [];
                          } catch {
                            jobData.assignedStaff = [];
                          }

                          if (jobData?.assignedStaff?.includes(staff.id)) {
                            jobData.assignedStaff.splice(
                              jobData.assignedStaff.indexOf(staff.id),
                              1
                            );
                          } else {
                            jobData.assignedStaff.push(staff.id);
                          }

                          await retryFirebaseUpdate(() =>
                            updateDoc(doc(db, "JobData", jobData.id), {
                              assignedStaff: jobData.assignedStaff,
                            })
                          );

                          await updateRecentlyAssignedStaff(staff.id);
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={jobData?.assignedStaff?.includes(staff.id)}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${staff.firstName} ${staff.lastName}`}
                        />
                      </ListItemButton>
                    </ListItem>
                  ) : null;
                })}
                <Divider />
              </>
            )}
            {/* All Staff Section */}
            {staffData &&
              staffData
                .filter((sd) => !recentlyAssignedStaff.includes(sd.id))
                .map((sd, index) => (
                  <ListItem disablePadding key={`listItem_${index}`}>
                    <ListItemButton
                      onClick={async () => {
                        try {
                          if (!jobData.hasOwnProperty("assignedStaff"))
                            jobData.assignedStaff = [];
                        } catch {
                          jobData.assignedStaff = [];
                        }

                        if (jobData?.assignedStaff?.includes(sd.id)) {
                          jobData.assignedStaff.splice(
                            jobData.assignedStaff.indexOf(sd.id),
                            1
                          );
                        } else {
                          jobData.assignedStaff.push(sd.id);
                        }

                        await retryFirebaseUpdate(() =>
                          updateDoc(doc(db, "JobData", jobData.id), {
                            assignedStaff: jobData.assignedStaff,
                          })
                        );

                        await updateRecentlyAssignedStaff(sd.id);
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={jobData?.assignedStaff?.includes(sd.id)}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${sd.firstName} ${sd.lastName}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
          </MUIList>
        </Collapse>
        <Divider />
        <Tooltip title="Toggles the hold tag and colour" placement="right">
          <MenuItem onClick={toggleHold}>Toggle Hold</MenuItem>
        </Tooltip>
        {isAdmin ? <Divider /> : null}
        {isAdmin ? (
          <Tooltip
            title="Will not delete any data, only the card"
            placement="right"
          >
            <MenuItem onClick={deleteCard}>Delete Card</MenuItem>
          </Tooltip>
        ) : null}
      </Menu>

      <motion.div
        className="list-card"
        onContextMenu={handleContextMenu}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{
          y: -2,
        }}
        transition={{
          duration: 0.2,
          ease: "easeOut",
        }}
      >
        <Paper
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          elevation={0}
          sx={{
            ...provided.draggableProps.style,
            backgroundColor: cardBackgroundColour,
            padding: 1.2,
            margin: "0.5px",
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            transform: isDragging ? "rotate(2deg) scale(1.02)" : "none",
            "&:hover": {
              boxShadow:
                theme === "dark"
                  ? "0px 3px 10px rgba(255,255,255,0.15) !important"
                  : "0px 3px 10px rgba(0,0,0,0.15) !important",
            },
          }}
          className={`item ${isDragging ? "is-dragging" : ""}`}
        >
          {!getJobName(jobData) ? (
            <Box
              sx={{
                height: 60,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: theme === "dark" ? "#ffffff" : "#1a202c",
                fontWeight: "bold",
                fontSize: "0.9rem",
              }}
            >
              Loading...
            </Box>
          ) : (
            <Box
              className="card-click-class"
              onClick={(e) => {
                setModalJobData(jobData);
                setModalClientData(jobClientData);
                setEventDataModalV2Open(true);
              }}
              sx={{
                display: clone ? "none" : "block",
                cursor: "pointer",
              }}
            >
              {jobData.idLabelsPermanent &&
                jobData.idLabelsPermanent.map((label, index) => (
                  <PermanentLabelChip
                    key={`${label}-${index}`}
                    label={label}
                    kanbanBoard={true}
                    jobData={jobData}
                  />
                ))}
              {jobData.idLabels &&
                jobData.idLabels.map((label, index) => (
                  <LabelChip
                    key={`${label}-${index}`}
                    label={label}
                    setLabelMenuOpen={() => {}}
                    kanbanBoard={true}
                  />
                ))}
              <Typography
                variant="subtitle2"
                className="card-click-class"
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                  color: theme === "dark" ? "#ffffff" : "#1a202c",
                  marginBottom: 0.5,
                  marginTop:
                    jobData.idLabels?.length > 0 ||
                    jobData.idLabelsPermanent?.length > 0
                      ? 0.5
                      : 0,
                  userSelect: "none",
                  lineHeight: 1.2,
                  display: "block",
                }}
              >
                {getJobName(jobData)}
              </Typography>
              <Box
                className="card-click-class"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 0.5,
                  marginTop: 0.5,
                  marginBottom: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.25,
                    alignItems: "center",
                  }}
                >
                  <DueDateChip
                    dueDate={jobData.dueDate}
                    complete={jobData.complete}
                    expressDueDate={jobData.expressDueDate}
                    expressComplete={jobData.expressComplete}
                    id={jobData.id}
                    theme={theme}
                    canEdit={true}
                    cardBackgroundColour={cardBackgroundColour}
                  />
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    <DaysInShootingChip jobData={jobData} />
                    <ChecklistsChip
                      jobData={jobData}
                      cardBackgroundColour={cardBackgroundColour}
                    />
                  </Box>
                  <ImagesChip
                    jobData={jobData}
                    clientData={clientData}
                    cardBackgroundColour={cardBackgroundColour}
                  />
                </Box>
              </Box>
              {jobData.assignedStaff && jobData.assignedStaff.length > 0 && (
                <AvatarGroup
                  sx={{
                    marginTop: 0.5,
                    marginBottom: 0.5,
                    "& .MuiAvatar-root": {
                      width: 28,
                      height: 28,
                      fontSize: "0.8rem",
                      border: `2px solid ${
                        theme === "dark" ? "#1e1e1e" : "#ffffff"
                      }`,
                    },
                  }}
                >
                  {jobData.assignedStaff.map((as, i) => {
                    const assignedData = returnStaffDataFromID(as, staffData);
                    if (!assignedData) return null;
                    const name = `${assignedData.firstName} ${assignedData.lastName}`;
                    return (
                      <Tooltip
                        title={name}
                        key={`assignedStaff_${name}_${i}`}
                        placement="top"
                      >
                        <Avatar
                          sx={{
                            bgcolor: stringToColor(name),
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                          }}
                          alt={name}
                          src={assignedData.avatar}
                        >
                          {!assignedData.avatar &&
                            name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                        </Avatar>
                      </Tooltip>
                    );
                  })}
                </AvatarGroup>
              )}
            </Box>
          )}
        </Paper>
      </motion.div>
    </>
  );
}

const returnStaffDataFromID = (id, staffData) => {
  if (!id || !staffData) return null;
  return staffData.find((sd) => sd.id === id) || null;
};

function stringToColor(string) {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

const DaysInShootingChip = ({ jobData }) => {
  const [labelColours] = useAtom(labelColoursAtom);
  const [theme] = useAtom(themeAtom);

  const [returnNull, setReturnNull] = useState(false);
  const [daysInShooting, setDaysInShooting] = useState(0);

  useEffect(() => {
    const main = () => {
      if (jobData.isInShooting) {
        setReturnNull(false);

        setDaysInShooting(
          getDaysDifference(
            jobData.isInShootingTime,
            DateTime.now()
              .setZone("Australia/Sydney")
              .startOf("day")
              .toUTC()
              .toISO()
          )
        );
      } else {
        setReturnNull(true);
        setDaysInShooting(0);
        return;
      }
    };
    main();
  }, [jobData]);

  function parseColour(days) {
    const defaultColour =
      theme === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)";
    if (days === 0) return defaultColour;
    if (days === 1)
      return labelColours["sky"][theme]
        ? labelColours["sky"][theme]
        : defaultColour;
    if (days === 2)
      return labelColours["yellow"][theme]
        ? labelColours["yellow"][theme]
        : defaultColour;
    if (days >= 3)
      return labelColours["red"][theme]
        ? labelColours["red"][theme]
        : defaultColour;
  }

  function getTextColor(days) {
    if (days === 0)
      return theme === "dark"
        ? "rgba(255, 255, 255, 0.8)"
        : "rgba(0, 0, 0, 0.8)";
    return "rgba(255, 255, 255, 0.95)";
  }

  if (returnNull) return null;

  return (
    <Chip
      label={`Days: ${daysInShooting}`}
      sx={{
        height: 32,
        fontSize: "0.75rem",
        fontWeight: 600,
        marginTop: 0.5,
        marginBottom: 0.5,
        color: getTextColor(daysInShooting),
        backgroundColor: parseColour(daysInShooting),
        borderRadius: 2,
        boxShadow:
          theme === "dark"
            ? "0 1px 4px rgba(0, 0, 0, 0.3)"
            : "0 1px 4px rgba(0, 0, 0, 0.08)",
        transition: "all 0.2s ease-in-out",
        "& .MuiChip-label": {
          paddingLeft: "4px",
          paddingRight: "12px",
          fontSize: "0.75rem",
          fontWeight: 600,
        },
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow:
            theme === "dark"
              ? "0 2px 6px rgba(0, 0, 0, 0.3)"
              : "0 2px 6px rgba(0, 0, 0, 0.1)",
        },
      }}
      size="small"
    />
  );
};

const ChecklistsChip = ({ jobData, cardBackgroundColour }) => {
  const [theme] = useAtom(themeAtom);
  const [labelColours] = useAtom(labelColoursAtom);
  const [returnNull, setReturnNull] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [labelText, setLabelText] = useState("0/0");

  useEffect(() => {
    const main = () => {
      if (!jobData.checklists) {
        setReturnNull(true);
        return;
      } else {
        setReturnNull(false);
      }
      let total = 0;
      let complete = 0;
      jobData.checklists.forEach((cl) => {
        if (cl?.checklistItems?.length > 0) {
          cl.checklistItems.forEach((ci) => {
            total = total + 1;
            if (ci.checked) complete = complete + 1;
          });
        }
      });
      if (total === 0 && complete === 0) {
        setReturnNull(true);
        return;
      }
      if (complete === total) {
        setIsComplete(true);
      } else {
        setIsComplete(false);
      }
      setLabelText(`${complete}/${total}`);
    };
    main();
  }, [jobData]);

  if (returnNull) return null;

  return (
    <Chip
      icon={
        <ChecklistIcon
          sx={{
            fontSize: "14px !important",
            color: isComplete
              ? "rgba(255, 255, 255, 0.9)"
              : theme === "dark"
              ? "rgba(255, 255, 255, 0.7)"
              : "rgba(0, 0, 0, 0.7)",
          }}
        />
      }
      label={labelText}
      sx={{
        height: 32,
        fontSize: "0.75rem",
        fontWeight: 600,
        marginTop: 0.5,
        marginBottom: 0.5,
        color: isComplete
          ? "rgba(255, 255, 255, 0.95)"
          : theme === "dark"
          ? "rgba(255, 255, 255, 0.9)"
          : "rgba(0, 0, 0, 0.9)",
        backgroundColor: isComplete
          ? labelColours["lime"][theme]
          : cardBackgroundColour || (theme === "dark" ? "#272727" : "#ebebeb"),
        borderRadius: 2,
        boxShadow:
          theme === "dark"
            ? "0 1px 4px rgba(0, 0, 0, 0.3)"
            : "0 1px 4px rgba(0, 0, 0, 0.08)",
        transition: "all 0.2s ease-in-out",
        "& .MuiChip-label": {
          paddingLeft: "4px",
          paddingRight: "12px",
          fontSize: "0.75rem",
          fontWeight: 600,
        },
        "& .MuiChip-icon": {
          fontSize: "1rem",
          marginLeft: "8px",
        },
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow:
            theme === "dark"
              ? "0 2px 6px rgba(0, 0, 0, 0.3)"
              : "0 2px 6px rgba(0, 0, 0, 0.1)",
        },
      }}
      size="small"
    />
  );
};

function getDaysDifference(startDateISOString, endDateISOString) {
  const startDate = DateTime.fromISO(startDateISOString);
  const endDate = DateTime.fromISO(endDateISOString);

  const diff = endDate.diff(startDate, "days").toObject();

  return Math.floor(diff.days);
}


const newShade = (hexColor, magnitude) => {
  if (!hexColor || typeof hexColor !== "string") return "#000000";
  hexColor = hexColor.replace(`#`, ``);
  if (hexColor.length === 6) {
    const decimalColor = parseInt(hexColor, 16);
    let r = (decimalColor >> 16) + magnitude;
    r > 255 && (r = 255);
    r < 0 && (r = 0);
    let g = (decimalColor & 0x0000ff) + magnitude;
    g > 255 && (g = 255);
    g < 0 && (g = 0);
    let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
    b > 255 && (b = 255);
    b < 0 && (b = 0);
    return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
  } else {
    return hexColor;
  }
};


function isJobTypeMatching(jobTypes, noteJobTypes, jobType) {
  let res = false;
  if (!noteJobTypes) {
    return true;
  }
  if (noteJobTypes.length === 0) {
    return true;
  }

  jobTypes.forEach((jt) => {
    const combined = `${jt.name} (-${jt.nameShort})`;
    if (combined === jobType) {
      noteJobTypes.forEach((njt) => {
        if (jt.id === njt) res = true;
      });
    }
  });
  return res;
}
